// talkingAvatarApi — HTTP client for the middleware's talking-avatar module.
//
//   GET  api/talking-avatar/models   → [{ id, label, beta, available, priced, capability }]
//        (`priced` = the model's video price row exists)
//        ?avatarId=<n> | ?avatarSource=upload → + avatarSupported, avatarUnsupportedReason
//        (NOT_AI_GENERATED | AVATAR_REJECTED | ASSET_LIBRARY_NOT_CONFIGURED — Seedance
//        needs an AI-created library avatar); without a query the list is unchanged
//   GET  api/talking-avatar/voice-providers
//        → [{ id: "elevenlabs" | "azure", label, beta, available, priced, voices? }]
//          (azure carries its conversion voices: [{ id, name, gender }])
//   POST api/talking-avatar/credit   { model, text, removeBackground, language?, voiceProvider? }
//                                    → quote (200 even when overLimit)
//   POST api/talking-avatar          { trainingId, navId, sceneIndex, model, text, voiceId,
//                                      voiceProvider?, aspectRatio, removeBackground, avatar,
//                                      language?, idempotencyKey? } → 202 { jobId, room }
//        (400 TALKING_AVATAR_MODEL_NOT_SUPPORTED_FOR_AVATAR carries a `reason` like the list)
//   GET  api/talking-avatar/:jobId   → { jobId, status, progress, stage, segment, result?, error? }
//
// voiceProvider absent = "elevenlabs". ElevenLabs voices come from the same
// routes the AI voice tab uses: stock voices from api/course/voices (their `id`
// IS the ElevenLabs voice id) and the org's cloned voices from
// api/tts-avatar/voices?providerName=private (DB `id` + ElevenLabs
// `providerId`). Azure voices come from the voice-providers list (their `id` is
// the SSML voice name). The job is always started with the voice's providerId.
//
// Every call goes through authedFetch (renews the token once on a 401) with
// quiet:true — failures are shown inline by the caller, not as a toast.
import { authedFetch } from "../../../../common/http_service.es6";
import { getOrganizationId } from "../../../../common/environment_config.es6";
import { isCreditRefusal } from "../../../utils/insufficientBalance";
import { normalizeTalkingAvatarErrorCode } from "./talkingAvatarErrors";

const QUIET = { quiet: true };
const VOICES_PAGE_SIZE = 12;

const apiUrl = (path) => `${process.env.MW_API_URL || ""}${path}`;

export class TalkingAvatarApiError extends Error {
  constructor(parsed, body) {
    super(parsed.message);
    this.name = "TalkingAvatarApiError";
    this.status = parsed.status;
    this.code = parsed.code;
    this.estimatedSeconds = parsed.estimatedSeconds;
    this.maxSeconds = parsed.maxSeconds;
    this.reason = parsed.reason;
    this.creditRefusal = parsed.creditRefusal;
    this.body = body;
  }
}

const asNumber = (v) => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() && Number.isFinite(Number(v))) return Number(v);
  return undefined;
};

const firstString = (...values) => values.find((v) => typeof v === "string" && v.trim());

function stringify(body) {
  if (typeof body === "string") return body;
  try {
    return JSON.stringify(body) || "";
  } catch (e) {
    return "";
  }
}

// Error code: body.code → error.code → metadata.error.code → Nest message
// object → a TALKING_AVATAR_* / PRICE_LIST_MISSING token anywhere in the body;
// a 429 without a code is the "≥3 active jobs" cap.
export function parseApiError(status, body) {
  const b = body && typeof body === "object" ? body : {};
  const msgObj =
    b.message && typeof b.message === "object" && !Array.isArray(b.message) ? b.message : null;
  const errObj = b.error && typeof b.error === "object" ? b.error : null;
  const mdErr =
    b.metadata && b.metadata.error && typeof b.metadata.error === "object"
      ? b.metadata.error
      : null;

  let code =
    firstString(b.code, errObj && errObj.code, mdErr && mdErr.code, msgObj && msgObj.code) || null;
  if (!code) {
    const m = /\b(TALKING_AVATAR_[A-Z_]+|PRICE_LIST_MISSING)\b/.exec(stringify(body));
    if (m) code = m[1];
  }
  // Every 429 is the "too many running jobs" cap: our own ≥3 rule carries the
  // code, the JobBus in-flight cap only a message.
  if (status === 429) code = "TALKING_AVATAR_TOO_MANY_ACTIVE";
  // Body validation (class-validator): message array, no code.
  if (!code && status === 400 && Array.isArray(b.message)) code = "INVALID_REQUEST";

  const message =
    firstString(
      typeof b.message === "string" ? b.message : null,
      Array.isArray(b.message) ? b.message.filter((x) => typeof x === "string").join("; ") : null,
      msgObj && msgObj.message,
      errObj && errObj.message,
      typeof b.error === "string" ? b.error : null,
      mdErr && mdErr.message,
      typeof body === "string" ? body : null
    ) || `HTTP ${status}`;

  return {
    status,
    code: normalizeTalkingAvatarErrorCode(code),
    message,
    estimatedSeconds: asNumber(
      b.estimatedSeconds ??
        (msgObj && msgObj.estimatedSeconds) ??
        (b.details && b.details.estimatedSeconds)
    ),
    maxSeconds: asNumber(
      b.maxSeconds ?? (msgObj && msgObj.maxSeconds) ?? (b.details && b.details.maxSeconds)
    ),
    // Why a model was refused for this avatar (MODEL_NOT_SUPPORTED_FOR_AVATAR).
    reason: firstString(
      b.reason,
      msgObj && msgObj.reason,
      errObj && errObj.reason,
      b.details && b.details.reason
    ),
    creditRefusal: isCreditRefusal(body),
  };
}

async function readJson(res) {
  try {
    return await res.json();
  } catch (e) {
    return null;
  }
}

async function request(path, { method = "GET", body, signal } = {}) {
  const opts = { method };
  if (body !== undefined) {
    opts.headers = { "Content-Type": "application/json" };
    opts.body = JSON.stringify(body);
  }
  if (signal) opts.signal = signal;
  const res = await authedFetch(apiUrl(path), opts, QUIET);
  const data = await readJson(res);
  if (!res.ok) throw new TalkingAvatarApiError(parseApiError(res.status, data), data);
  return data;
}

// ── Models (memoized per avatar query for the page's lifetime; a failure is not cached) ──
const modelsPromises = new Map();
const modelLabels = {};

// "Veo" ids run Gemini Omni since 2026-10-23: one model, the two tiers differ
// only by resolution. The picker shows a single "Omni" card (veo-3.1) and hides
// veo-3.1-fast; the labels are pinned here too in case the server still sends
// the old Veo names.
const OMNI_MODEL_LABELS = { "veo-3.1": "Omni", "veo-3.1-fast": "Omni (720p)" };
const HIDDEN_MODEL_IDS = new Set(["veo-3.1-fast"]);

const normalizeModel = (m) => ({
  ...m,
  label: OMNI_MODEL_LABELS[m.id] || m.label,
  available: m.available !== false,
  priced: m.priced !== false,
  avatarSupported: m.avatarSupported !== false,
});

const modelsQueryString = ({ avatarId, avatarSource } = {}) => {
  if (avatarId !== undefined && avatarId !== null && avatarId !== "") {
    return `?avatarId=${encodeURIComponent(avatarId)}`;
  }
  if (avatarSource === "upload") return "?avatarSource=upload";
  return "";
};

// query: { avatarId } (a library avatar) | { avatarSource: "upload" } | {} (no avatar yet).
export function fetchTalkingAvatarModels(query = {}) {
  const qs = modelsQueryString(query || {});
  if (!modelsPromises.has(qs)) {
    const promise = request(`api/talking-avatar/models${qs}`)
      .then((data) => {
        const list = Array.isArray(data) ? data : (data && data.models) || [];
        const models = list.filter((m) => m && m.id).map(normalizeModel);
        models.forEach((m) => {
          modelLabels[m.id] = m.label || m.id;
        });
        const hasOmni = models.some((m) => m.id === "veo-3.1");
        return hasOmni ? models.filter((m) => !HIDDEN_MODEL_IDS.has(m.id)) : models;
      })
      .catch((err) => {
        modelsPromises.delete(qs);
        throw err;
      });
    modelsPromises.set(qs, promise);
  }
  return modelsPromises.get(qs);
}

// ── Voice providers (memoized like the models; a failure is not cached) ─────
let voiceProvidersPromise = null;

const normalizeVoiceProvider = (p) => {
  const id = String(p.id).trim().toLowerCase();
  return {
    ...p,
    id,
    label: p.label || (id === "elevenlabs" ? "ElevenLabs" : String(p.id)),
    beta: !!p.beta,
    available: p.available !== false,
    priced: p.priced !== false,
    voices: Array.isArray(p.voices) ? p.voices.filter((v) => v && v.id) : [],
    // Azure only: every Azure TTS voice — what audio-driven models may speak with.
    ttsVoices: Array.isArray(p.ttsVoices) ? p.ttsVoices.filter((v) => v && v.id) : [],
  };
};

export function fetchTalkingAvatarVoiceProviders() {
  if (!voiceProvidersPromise) {
    voiceProvidersPromise = request("api/talking-avatar/voice-providers")
      .then((data) => {
        const list = Array.isArray(data) ? data : (data && data.providers) || [];
        return list.filter((p) => p && p.id).map(normalizeVoiceProvider);
      })
      .catch((err) => {
        voiceProvidersPromise = null;
        throw err;
      });
  }
  return voiceProvidersPromise;
}

// Samples for the Azure conversion voices: the list the AI voice tab plays its
// Azure "multilingual" voices from (free, pre-generated — no TTS job). A voice
// missing here simply has no sample.
let azureSamplesPromise = null;

export function fetchAzureVoiceSamples() {
  if (!azureSamplesPromise) {
    const params = new URLSearchParams();
    params.set("service", "microsoft");
    params.set("language", "multilingual");
    params.set("page", "1");
    params.set("pageSize", "200");
    azureSamplesPromise = request(`api/course/voices?${params.toString()}`)
      .then((data) => (data && Array.isArray(data.voices) ? data.voices : []))
      .catch((err) => {
        azureSamplesPromise = null;
        throw err;
      });
  }
  return azureSamplesPromise;
}

// Quote for the footer popover too: { requiredCredits, availableCredits,
// hasSufficientBalance, providers: [{ label, cost }] } plus the length plan.
export async function estimateTalkingAvatarCredit(
  { model, text, removeBackground, language, voiceProvider },
  { signal } = {}
) {
  const data =
    (await request("api/talking-avatar/credit", {
      method: "POST",
      body: {
        model,
        text,
        removeBackground: !!removeBackground,
        ...(language ? { language } : {}),
        ...(voiceProvider ? { voiceProvider } : {}),
      },
      signal,
    })) || {};
  const requiredCredits = asNumber(data.requiredCredits) || 0;
  const availableCredits = asNumber(data.availableCredits) || 0;
  return {
    requiredCredits,
    availableCredits,
    hasSufficientBalance:
      typeof data.hasSufficientBalance === "boolean"
        ? data.hasSufficientBalance
        : requiredCredits <= availableCredits,
    estimatedSeconds: asNumber(data.estimatedSeconds),
    maxSeconds: asNumber(data.maxSeconds),
    segments: asNumber(data.segments),
    overLimit: !!data.overLimit,
    providers: [{ label: modelLabels[model] || model, cost: requiredCredits }],
  };
}

export async function submitTalkingAvatarJob(body) {
  const data = (await request("api/talking-avatar", { method: "POST", body })) || {};
  return { jobId: data.jobId, room: data.room };
}

export function getTalkingAvatarJob(jobId, { signal } = {}) {
  return request(`api/talking-avatar/${encodeURIComponent(jobId)}`, { signal });
}

export async function fetchElevenLabsVoices({
  gender,
  page = 1,
  pageSize = VOICES_PAGE_SIZE,
} = {}) {
  const params = new URLSearchParams();
  params.set("service", "elevenlabs");
  params.set("language", "multilingual");
  params.set("page", String(page));
  params.set("pageSize", String(pageSize));
  if (gender && gender !== "all") params.set("gender", gender);
  const data = (await request(`api/course/voices?${params.toString()}`)) || {};
  const pagination = data.pagination || {};
  const total = asNumber(pagination.total ?? data.total ?? data.totalCount);
  return {
    voices: Array.isArray(data.voices) ? data.voices : [],
    page: asNumber(pagination.page) || page,
    totalPages:
      asNumber(pagination.totalPages ?? data.totalPages) ||
      (total ? Math.ceil(total / pageSize) : 1),
    total: total ?? null,
  };
}

// The org comes from the JWT on the server; the param mirrors AIVoiceTab.
export async function fetchOrgPrivateVoices() {
  const params = new URLSearchParams();
  const organizationId = getOrganizationId && getOrganizationId();
  if (organizationId) params.set("organizationId", organizationId);
  params.set("providerName", "private");
  const data = await request(`api/tts-avatar/voices?${params.toString()}`);
  if (Array.isArray(data)) return data;
  return (data && Array.isArray(data.voices) && data.voices) || [];
}

// Test helper.
export function __resetTalkingAvatarApi() {
  modelsPromises.clear();
  voiceProvidersPromise = null;
  azureSamplesPromise = null;
  Object.keys(modelLabels).forEach((k) => delete modelLabels[k]);
}
